/**
 * User Management — detail view.
 *
 * Three sections (cards) compose the page:
 *
 *   1. Profile — editable form for the core User fields
 *      (name, email, phone, username, role, is_active, is_staff). Includes
 *      a one-shot password reset action that returns a temp password the
 *      admin must copy out-of-band. EmployeeInfo / RiderInfo are shown
 *      read-only with deep links to their existing pages.
 *
 *   2. Access Control — read-only summary using
 *      `GET /feature/bi/api/admin/preview/users/:id/`. The "Manage in
 *      Access Control" button deep-links to /settings/access?user=<id>,
 *      which opens the grant matrix focused on this user.
 *
 *   3. CRM links — read-only routing-target rows + active assignments
 *      from `GET /api/bi/crm/users/:id/links/`.
 */
import { useEffect, useMemo, useState } from 'react';
import { ChevronRight, KeyRound, Save, ShieldCheck, Ticket as TicketIcon, UserCog } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { ErrorState } from '@/components/ui/ErrorState';
import { Skeleton } from '@/components/ui/Skeleton';
import { ToggleChip } from '@/components/ui/Chip';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { PageHeader } from '@/components/layout/PageHeader';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/Dialog';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import {
  useInternalUser,
  useUpdateInternalUser,
  useResetUserPassword,
  useUserCrmLinks,
  useUserAccessPreview,
  type InternalUserPatch,
  type ResetPasswordResponse,
} from '@/api/bi/internalUsers';
import { useAuth } from '@/auth/AuthProvider';
import { extractErrorMessage } from '@/api/client';
import { notify } from '@/lib/toast';
import { cn } from '@/lib/cn';

const ROLE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'admin', label: 'Admin' },
  { value: 'manager', label: 'Store Manager' },
  { value: 'support', label: 'Support' },
  { value: 'operation', label: 'Operation' },
  { value: 'account', label: 'Account' },
  { value: 'staff', label: 'Staff Member' },
  { value: 'packer', label: 'Packer' },
  { value: 'rider', label: 'Rider' },
  { value: 'driver', label: 'Driver' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'agency', label: 'Agency' },
  { value: 'tagger', label: 'Prod Tagger' },
  { value: 'audit', label: 'Audit' },
  { value: 'guard', label: 'Guard' },
];

/** Sentinel for "no role" (used when is_staff=true). */
const NO_ROLE = '__no_role__';

interface ProfileForm {
  name: string;
  email: string;
  phone_number: string;
  username: string;
  role: string;
  is_active: boolean;
  is_staff: boolean;
}

function formFromUser(user: ReturnType<typeof useInternalUser>['data']): ProfileForm {
  return {
    name: user?.name ?? '',
    email: user?.email ?? '',
    phone_number: user?.phone_number ?? '',
    username: user?.username ?? '',
    role: user?.role ?? NO_ROLE,
    is_active: user?.is_active ?? true,
    is_staff: user?.is_staff ?? false,
  };
}

function patchDiff(initial: ProfileForm, current: ProfileForm): InternalUserPatch {
  const out: InternalUserPatch = {};
  if (initial.name !== current.name) out.name = current.name;
  if (initial.email !== current.email) out.email = current.email || null;
  if (initial.phone_number !== current.phone_number) out.phone_number = current.phone_number || null;
  if (initial.username !== current.username) out.username = current.username || null;
  if (initial.role !== current.role) {
    out.role = current.role === NO_ROLE ? null : current.role;
  }
  if (initial.is_active !== current.is_active) out.is_active = current.is_active;
  if (initial.is_staff !== current.is_staff) out.is_staff = current.is_staff;
  return out;
}

export function UserManagementPage() {
  const params = useParams();
  const navigate = useNavigate();
  const { user: actor } = useAuth();
  const userId = params.id ? Number(params.id) : null;

  const detail = useInternalUser(userId);
  const update = useUpdateInternalUser(userId);
  const resetPassword = useResetUserPassword(userId);
  const access = useUserAccessPreview(userId);
  const crmLinks = useUserCrmLinks(userId);

  const [form, setForm] = useState<ProfileForm>(() => formFromUser(undefined));
  const [initial, setInitial] = useState<ProfileForm>(() => formFromUser(undefined));
  const [resetResult, setResetResult] = useState<ResetPasswordResponse | null>(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  // Sync form once the user payload loads / changes.
  useEffect(() => {
    if (detail.data) {
      const next = formFromUser(detail.data);
      setForm(next);
      setInitial(next);
    }
  }, [detail.data]);

  const dirty = useMemo(() => {
    return JSON.stringify(form) !== JSON.stringify(initial);
  }, [form, initial]);

  const isSelf = userId != null && actor?.id === userId;

  if (userId == null) {
    return <ErrorState title="Missing user id" />;
  }

  function update_<K extends keyof ProfileForm>(key: K, value: ProfileForm[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function save() {
    const diff = patchDiff(initial, form);
    if (Object.keys(diff).length === 0) {
      notify.info('No changes to save.');
      return;
    }
    try {
      await update.mutateAsync(diff);
      notify.success('Profile updated');
    } catch (err) {
      notify.error(extractErrorMessage(err, 'Could not update user'));
    }
  }

  async function doResetPassword() {
    try {
      const data = await resetPassword.mutateAsync();
      setResetResult(data);
      setConfirmReset(false);
      setResetOpen(true);
    } catch (err) {
      notify.error(extractErrorMessage(err, 'Could not reset password'));
    }
  }

  const detailUser = detail.data;

  return (
    <>
      <PageHeader
        breadcrumbs={[
          { label: 'Settings', to: '/settings' },
          { label: 'Users', to: '/settings/users' },
          { label: `#${userId}` },
        ]}
        eyebrow="User Management"
        title={detailUser?.name || detailUser?.username || `User #${userId}`}
        description={
          detail.isLoading ? (
            'Loading…'
          ) : detailUser ? (
            <span className="inline-flex items-center gap-2 text-xs text-muted-foreground">
              {detailUser.is_active ? <Badge tone="success">active</Badge> : <Badge tone="danger">inactive</Badge>}
              {detailUser.is_superuser ? <Badge tone="primary">superuser</Badge> : null}
              {detailUser.role ? (
                <Badge tone="neutral" className="capitalize">{detailUser.role}</Badge>
              ) : detailUser.is_staff ? (
                <Badge tone="neutral">staff (django)</Badge>
              ) : null}
              {detailUser.username ? <span>@{detailUser.username}</span> : null}
            </span>
          ) : null
        }
      />

      {detail.isError ? (
        <div className="mt-4">
          <ErrorState onRetry={() => void detail.refetch()} />
        </div>
      ) : null}

      {/* Summary chips — sourced from the detail endpoint's `summary` block. */}
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryStat label="Tickets created" value={detailUser?.summary?.tickets_created} />
        <SummaryStat label="Tickets resolved" value={detailUser?.summary?.tickets_resolved} />
        <SummaryStat label="Currently assigned" value={detailUser?.summary?.tickets_currently_assigned} />
        <SummaryStat label="Routing rules targeted" value={detailUser?.summary?.routing_rules_targeted} />
      </div>

      {/* ----------------------------- PROFILE -------------------------------- */}
      <Card className="mt-4">
        <CardHeader>
          <CardTitle className="inline-flex items-center gap-2">
            <UserCog className="h-4 w-4" /> Profile
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setConfirmReset(true)}
              disabled={resetPassword.isPending || !detailUser}
              className="gap-1"
            >
              <KeyRound className="h-3.5 w-3.5" /> Reset password
            </Button>
            <Button
              size="sm"
              variant="primary"
              onClick={() => void save()}
              disabled={!dirty || update.isPending}
              className="gap-1"
            >
              <Save className="h-3.5 w-3.5" /> Save
            </Button>
          </div>
        </CardHeader>
        <CardBody className="space-y-4 text-sm">
          {detail.isLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : (
            <>
              <div className="grid gap-3 md:grid-cols-2">
                <Field label="Full name">
                  <Input
                    value={form.name}
                    onChange={(e) => update_('name', e.target.value)}
                  />
                </Field>
                <Field label="Username">
                  <Input
                    value={form.username}
                    onChange={(e) => update_('username', e.target.value)}
                  />
                </Field>
                <Field label="Phone number">
                  <Input
                    value={form.phone_number}
                    onChange={(e) => update_('phone_number', e.target.value)}
                  />
                </Field>
                <Field label="Email">
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => update_('email', e.target.value)}
                  />
                </Field>
                <Field
                  label="Role"
                  hint={
                    form.is_staff
                      ? "Staff (django) users must NOT have a role; pick 'No role' to keep is_staff."
                      : 'Required for non-staff accounts.'
                  }
                >
                  <Select
                    value={form.role}
                    onValueChange={(v) => update_('role', v)}
                  >
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_ROLE}>No role (Django staff)</SelectItem>
                      {ROLE_OPTIONS.map((r) => (
                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Status">
                  <div className="flex flex-wrap items-center gap-2">
                    <StatusToggle
                      checked={form.is_active}
                      onChange={(v) => update_('is_active', v)}
                      onLabel="Active"
                      offLabel="Inactive"
                      toneOn="success"
                      disabled={isSelf}
                      hint={isSelf ? 'You cannot deactivate your own account.' : undefined}
                    />
                    <StatusToggle
                      checked={form.is_staff}
                      onChange={(v) => update_('is_staff', v)}
                      onLabel="Django staff"
                      offLabel="Not staff"
                      toneOn="info"
                    />
                  </div>
                </Field>
              </div>

              <div className="grid gap-3 border-t border-border pt-4 md:grid-cols-3">
                <ReadKV label="Last login" value={detailUser?.last_login ? format(new Date(detailUser.last_login), 'MMM d, HH:mm') : '—'} />
                <ReadKV label="Last active" value={detailUser?.last_active ? format(new Date(detailUser.last_active), 'MMM d, HH:mm') : '—'} />
                <ReadKV label="Joined" value={detailUser?.date_joined ? format(new Date(detailUser.date_joined), 'MMM d, yyyy') : '—'} />
              </div>

              {/* Related profile rows — read-only, deep-link to existing pages */}
              {(detailUser?.employee_info || detailUser?.rider_info || (detailUser?.user_info?.length ?? 0) > 0) ? (
                <div className="grid gap-3 border-t border-border pt-4 md:grid-cols-2">
                  {detailUser?.employee_info ? (
                    <RelatedBlock
                      title="Employee info"
                      onEdit={() => navigate('/roster')}
                      rows={[
                        ['Store', detailUser.employee_info.store?.name ?? '—'],
                        ['Document', detailUser.employee_info.document_type ?? '—'],
                        ['Phone', detailUser.employee_info.phone_number ?? '—'],
                        ['Online / Available', `${detailUser.employee_info.is_online ? 'online' : 'offline'} · ${detailUser.employee_info.is_available ? 'available' : 'busy'}`],
                      ]}
                    />
                  ) : null}
                  {detailUser?.rider_info ? (
                    <RelatedBlock
                      title="Rider info"
                      onEdit={() => navigate('/riders')}
                      rows={[
                        ['Store', detailUser.rider_info.store?.name ?? '—'],
                        ['Document', detailUser.rider_info.document_type ?? '—'],
                        ['Phone', detailUser.rider_info.phone_number ?? '—'],
                        ['Emergency contact', detailUser.rider_info.emergency_contact_number ?? '—'],
                        ['Uses cycle', detailUser.rider_info.uses_cycle ? 'yes' : 'no'],
                      ]}
                    />
                  ) : null}
                </div>
              ) : null}
            </>
          )}
        </CardBody>
      </Card>

      {/* -------------------------- ACCESS CONTROL ---------------------------- */}
      <Card className="mt-4">
        <CardHeader>
          <CardTitle className="inline-flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" /> Access control
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate(`/settings/access?user=${userId}`)}
              className="gap-1"
            >
              Manage in Access Control <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </CardHeader>
        <CardBody className="space-y-3 text-sm">
          <p className="text-muted-foreground">
            Effective permissions for this user combine their role grants and personal overrides.
            Use the link above to edit overrides in the existing Access Control panel.
          </p>
          {access.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : access.isError ? (
            <ErrorState onRetry={() => void access.refetch()} />
          ) : (() => {
            const list = access.data?.resources ?? [];
            const viewable = list.filter((r) => r.permissions?.view);
            if (!viewable.length) {
              return (
                <div className="rounded-md border border-dashed border-border p-4 text-center text-xs text-muted-foreground">
                  No BI resources granted yet.
                </div>
              );
            }
            return (
              <div className="overflow-x-auto rounded-md border border-border">
                <table className="w-full min-w-[640px] text-xs">
                  <thead className="bg-muted/50 text-left uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 font-medium">Resource</th>
                      <th className="px-3 py-2 font-medium">View</th>
                      <th className="px-3 py-2 font-medium">Export</th>
                      <th className="px-3 py-2 font-medium">Manage</th>
                      <th className="px-3 py-2 font-medium">Schedule</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {viewable.map((r) => (
                      <tr key={r.slug}>
                        <td className="px-3 py-2">
                          <p className="font-medium">{r.name}</p>
                          <p className="font-mono text-[10px] text-muted-foreground">{r.slug}</p>
                        </td>
                        <PermCell on={r.permissions.view} />
                        <PermCell on={r.permissions.export} />
                        <PermCell on={r.permissions.manage} />
                        <PermCell on={r.permissions.schedule} />
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </CardBody>
      </Card>

      {/* ----------------------------- CRM LINKS ------------------------------ */}
      <Card className="mt-4">
        <CardHeader>
          <CardTitle className="inline-flex items-center gap-2">
            <TicketIcon className="h-4 w-4" /> CRM links
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate('/settings/support/routing')}
              className="gap-1"
            >
              Routing matrix <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </CardHeader>
        <CardBody className="space-y-4 text-sm">
          {crmLinks.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : crmLinks.isError ? (
            <ErrorState onRetry={() => void crmLinks.refetch()} />
          ) : (
            <>
              {/* Routing rules */}
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Routing rules ({crmLinks.data?.routing_targets.length ?? 0})
                </p>
                {crmLinks.data?.routing_targets.length ? (
                  <ul className="space-y-1.5">
                    {crmLinks.data.routing_targets.map((t) => (
                      <li
                        key={t.id}
                        className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-surface px-3 py-2"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-medium">
                            {t.rule.name}
                            {!t.rule.is_enabled ? (
                              <Badge tone="warning" className="ml-2">disabled</Badge>
                            ) : null}
                          </p>
                          <p className="text-[11px] text-muted-foreground">
                            Tier #{t.tier.position}
                            {t.tier.label ? ` (${t.tier.label})` : ''} · evaluation order {t.rule.evaluation_order}
                          </p>
                        </div>
                        <Badge
                          tone={t.flag === 'primary' ? 'primary' : t.flag === 'watcher' ? 'info' : 'neutral'}
                          className="capitalize"
                        >
                          {t.flag}
                        </Badge>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="rounded-md border border-dashed border-border p-4 text-center text-xs text-muted-foreground">
                    Not referenced by any routing rule.
                  </div>
                )}
              </div>

              {/* Active assignments */}
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Active ticket assignments ({crmLinks.data?.active_assignments.length ?? 0})
                </p>
                {crmLinks.data?.active_assignments.length ? (
                  <ul className="space-y-1.5">
                    {crmLinks.data.active_assignments.map((a) => (
                      <li
                        key={a.id}
                        className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-surface px-3 py-2"
                      >
                        <div className="min-w-0 flex-1">
                          <Link
                            to={`/crm/tickets/${a.ticket.id}`}
                            className="truncate font-medium hover:underline"
                          >
                            #{a.ticket.id} · {a.ticket.summary || a.ticket.type}
                          </Link>
                          <p className="text-[11px] text-muted-foreground">
                            {a.ticket.type} · {a.ticket.status} · {a.ticket.priority}
                            {a.ticket.created_at
                              ? ` · created ${format(new Date(a.ticket.created_at), 'MMM d, HH:mm')}`
                              : ''}
                          </p>
                        </div>
                        {a.is_primary ? <Badge tone="primary">primary</Badge> : null}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="rounded-md border border-dashed border-border p-4 text-center text-xs text-muted-foreground">
                    No open tickets assigned to this user.
                  </div>
                )}
              </div>
            </>
          )}
        </CardBody>
      </Card>

      <ConfirmDialog
        open={confirmReset}
        onOpenChange={setConfirmReset}
        title="Issue a temporary password?"
        description="The current password stops working immediately, and this user is signed out of every session."
        details={
          detailUser ? (
            <span>
              For <span className="font-medium">{detailUser.username}</span>
              {detailUser.email ? ` (${detailUser.email})` : ''}. You will need to pass the new
              password on yourself — it is shown once and not emailed.
            </span>
          ) : null
        }
        confirmLabel="Reset password"
        tone="destructive"
        isPending={resetPassword.isPending}
        onConfirm={() => void doResetPassword()}
      />

      {/* Reset password result modal */}
      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent>
          <DialogTitle className="text-base font-semibold">Temporary password issued</DialogTitle>
          <DialogDescription className="mt-1 text-xs text-muted-foreground">
            This is the only time the password will be shown. Copy it now and share it through a secure channel.
          </DialogDescription>
          <div className="mt-4 rounded-md border border-border bg-muted/40 p-3">
            <code className="select-all break-all font-mono text-sm">
              {resetResult?.temporary_password}
            </code>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                if (resetResult?.temporary_password && navigator.clipboard) {
                  void navigator.clipboard
                    .writeText(resetResult.temporary_password)
                    .then(() => notify.success('Copied to clipboard'));
                }
              }}
            >
              Copy
            </Button>
            <Button onClick={() => setResetOpen(false)}>Done</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Helper components
// ---------------------------------------------------------------------------

function SummaryStat({ label, value }: { label: string; value: number | undefined }) {
  return (
    <Card>
      <CardBody>
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="font-display kpi-num mt-1 text-2xl font-bold">
          {value ?? '—'}
        </div>
      </CardBody>
    </Card>
  );
}

function ReadKV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-sm">{value ?? '—'}</p>
    </div>
  );
}

/** Chip whose label states the current setting, with a dot echoing the tone. */
function StatusToggle({
  checked,
  onChange,
  onLabel,
  offLabel,
  toneOn = 'success',
  disabled,
  hint,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  onLabel: string;
  offLabel: string;
  toneOn?: 'success' | 'info';
  disabled?: boolean;
  hint?: string;
}) {
  return (
    <ToggleChip
      active={checked}
      tone={toneOn}
      onClick={() => onChange(!checked)}
      disabled={disabled}
      title={hint}
      className="h-8 rounded-md px-2.5 text-xs font-medium"
      icon={
        <span
          aria-hidden
          className={cn(
            'inline-block h-2 w-2 rounded-full',
            checked ? (toneOn === 'success' ? 'bg-success' : 'bg-info') : 'bg-muted-foreground/40',
          )}
        />
      }
    >
      {checked ? onLabel : offLabel}
    </ToggleChip>
  );
}

function RelatedBlock({
  title,
  rows,
  onEdit,
}: {
  title: string;
  rows: Array<[string, React.ReactNode]>;
  onEdit?: () => void;
}) {
  return (
    <div className="rounded-md border border-border bg-surface p-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</p>
        {onEdit ? (
          <Button size="sm" variant="ghost" onClick={onEdit} className="h-7 gap-1 text-[11px]">
            Manage <ChevronRight className="h-3 w-3" />
          </Button>
        ) : null}
      </div>
      <div className="mt-2 space-y-1.5">
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-baseline justify-between gap-2 text-xs">
            <span className="text-muted-foreground">{k}</span>
            <span className="text-right font-medium">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PermCell({ on }: { on: boolean | undefined }) {
  return (
    <td className="px-3 py-2">
      {on ? (
        <Badge tone="success">allow</Badge>
      ) : (
        <span className="text-xs text-muted-foreground/70">—</span>
      )}
    </td>
  );
}
